import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, Observable, throwError } from "rxjs";
import { LogService } from "../../shared/service/logging/log.service";
import { Router } from "@angular/router";
import { ApiError } from "../../shared/models/api-error.model";

@Injectable({ providedIn: "root" })
export class ApiErrorInterceptor implements HttpInterceptor {
  private readonly logger = inject(LogService);
  private readonly router = inject(Router);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error) => {
        const apiError = this.normalizeError(error);

        if (this.logger.shouldLog(apiError)) {
          this.logger.error("Api error interceptor: ", apiError);
        }

        this.handleHttpStatus(apiError.status);

        // TODO: Maybe pass on the errors to a global error handler service?
        // TODO: Centralizing how we handle the errors and showcase them to the users?

        return throwError(() => apiError);
      })
    );
  }

  private normalizeError(error: unknown): ApiError {
    if (error instanceof HttpErrorResponse) {
      const status = error.status || 0;
      const rawPayload = error.error;

      let message: string | undefined = undefined;
      let details: string[] | undefined = undefined;

      if (rawPayload && typeof rawPayload === "string") {
        message = rawPayload;
      } else if (rawPayload && typeof rawPayload === "object") {
        const maybeApiError = rawPayload as Partial<ApiError>;

        if (typeof maybeApiError.message === "string") {
          message = maybeApiError.message;
        }

        if (
          Array.isArray(maybeApiError.details) &&
          maybeApiError.details.every((e) => typeof e === "string")
        ) {
          details = maybeApiError.details;
        } else if (
          "errors" in rawPayload &&
          typeof rawPayload.errors === "object" &&
          rawPayload.errors !== null
        ) {
          // Flatten ModelStateDictionary-style errors
          details = Object.values(rawPayload.errors)
            .flat()
            .filter((e): e is string => typeof e === "string");
        }
      }

      // Handle status code 0
      if (!navigator.onLine && status === 0) {
        message = "No network connection."
      }

      message ||= details?.length
        ? "Something went wrong. See details for more info."
        : "An unexpected error occurred";

      return {
        message,
        details,
        status,
        raw: error,
        error: rawPayload,
      };
    }

    return {
      message: "An unknown error occurred",
      status: 0,
    };
  }

  private handleHttpStatus(status: number): void {
    switch (status) {
      case HttpStatusCode.NotFound:
        // TODO: Either enable global 404 page usage, for all 404 requests,
        // TODO: or switch to using custom handling per component e.g. in event and community
        // this.router.navigate(["/not-found"]);
        break; // TODO: Add more cases as needed
      default:
        break;
    }
  }
}
